import { useCallback, useRef, useState } from "react";
import { resolveInstrument, setMarketDataPreference } from "../services/instrumentsApi";
import { resolveInstrumentForOhlcMenu } from "../utils/instrumentResolveOhlcMenu";
import type { MarketDataAttribution } from "../types/marketDataAttribution";
import type {
  MarketDataAssetClassRequiredBody,
  MarketDataCandidateBroker,
  MarketDataConnectOption,
  ResolveInstrumentResponse,
} from "../types/instruments";
import type { QuoteVariantCandidate } from "../types/instrumentSetup";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import {
  availableInstrumentRowToResolveResult,
  isInstrumentMarketDataReadyForCharts,
} from "../utils/instrumentMarketDataTrust";
import {
  buildInstrumentResolveRequest,
  isQuoteVariantPickerRequired,
  normalizeQuoteVariantCandidates,
  resolveMarketDataSourcesForQuoteVariant,
  resolvedSymbolForInstrumentResolve,
} from "../utils/instrumentSetupSave";
import { buildMarketDataPreferenceRequest } from "../utils/marketDataPreferenceSave";
import { isBrokerConnectMarketDataOption } from "../utils/marketDataConnectGate";
import {
  defaultMarketDataSourceOption,
  hasEffectiveMarketDataSourceSelection,
  isIbkrMarketDataBroker,
  marketDataBrokerKey,
  needsMarketDataSourceChoiceForResolve,
  needsMarketDataSourceChoiceForRow,
  resolveMarketDataSourceOptions,
} from "../utils/marketDataSourceChoice";

export type InstrumentResolveUiState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "resolved"; result: ResolveInstrumentResponse }
  | { kind: "error"; message: string };

export type PendingMarketDataSourceChoice = {
  symbol: string;
  asset_class?: string;
  instrument_id?: string;
  /** Qualified OHLC symbol when source choice follows a quote-variant pick (e.g. AAVEUSDT). */
  resolved_symbol?: string;
  sources: MarketDataSourceOption[];
};

export type PendingQuoteVariantChoice = {
  tradeSymbol: string;
  asset_class?: string;
  instrument_id?: string;
  candidates: QuoteVariantCandidate[];
  suggested?: string | null;
};

export type PendingMarketDataConsent = {
  md_listing_key: string;
  symbol: string;
  asset_class?: string;
  market_data_broker?: string;
  attribution?: MarketDataAttribution;
  source?: MarketDataSourceOption;
};

export type PendingConnectorRequired = {
  symbol: string;
  asset_class?: string;
  message?: string;
  connect_options: MarketDataConnectOption[];
  market_data_sources?: MarketDataSourceOption[];
};

type ApplyResolveResultOptions = {
  /** User already picked spot/futures (or similar) — do not reopen OHLC modal. */
  skipMarketDataSourceChoicePrompt?: boolean;
  /** Trade label when resolve symbol differs (quote variant). */
  tradeSymbol?: string;
};

function needsAssetClassConfirmation(result: ResolveInstrumentResponse): boolean {
  return (
    result.error === "market_data_asset_class_required" ||
    Boolean(result.requires_asset_class_confirmation)
  );
}

function needsQuoteVariantConfirmation(result: ResolveInstrumentResponse): boolean {
  return (
    isQuoteVariantPickerRequired(result) &&
    (result.quote_variant_candidates?.length ?? 0) > 0
  );
}

function resolveRequiresMarketDataConsent(
  result: ResolveInstrumentResponse
): result is ResolveInstrumentResponse & { md_listing_key: string } {
  if (!result.requires_market_data_consent || !result.md_listing_key) return false;
  const activeBroker = marketDataBrokerKey(
    result.selected_market_data_source ?? { market_data_broker: result.market_data_broker }
  );
  return isBrokerConnectMarketDataOption(activeBroker);
}

export function useInstrumentResolve(brokerAccountCode: string) {
  const [state, setState] = useState<InstrumentResolveUiState>({ kind: "idle" });
  const [pendingSymbol, setPendingSymbol] = useState<string | null>(null);
  const [pendingAssetClassPayload, setPendingAssetClassPayload] =
    useState<MarketDataAssetClassRequiredBody | null>(null);
  const [pendingQuoteVariantChoice, setPendingQuoteVariantChoice] =
    useState<PendingQuoteVariantChoice | null>(null);
  const [pendingMarketDataSourceChoice, setPendingMarketDataSourceChoice] =
    useState<PendingMarketDataSourceChoice | null>(null);
  const [pendingConsent, setPendingConsent] = useState<PendingMarketDataConsent | null>(null);
  const [pendingConnectorRequired, setPendingConnectorRequired] =
    useState<PendingConnectorRequired | null>(null);
  const [savingMarketDataSource, setSavingMarketDataSource] = useState(false);
  const [savingQuoteVariant, setSavingQuoteVariant] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const pendingMarketDataSourceChoiceRef = useRef(pendingMarketDataSourceChoice);
  pendingMarketDataSourceChoiceRef.current = pendingMarketDataSourceChoice;
  const resolveInFlightRef = useRef(
    new Map<string, Promise<ResolveInstrumentResponse | null>>()
  );

  const clearPendingGates = useCallback(() => {
    setPendingAssetClassPayload(null);
    setPendingQuoteVariantChoice(null);
    setPendingMarketDataSourceChoice(null);
    setPendingConsent(null);
    setPendingConnectorRequired(null);
  }, []);

  const openMarketDataSourceChoice = useCallback(
    (input: {
      symbol: string;
      asset_class?: string;
      instrument_id?: string;
      resolved_symbol?: string;
      market_data_sources?: MarketDataSourceOption[];
      candidate_brokers?: MarketDataCandidateBroker[];
      quoteCandidates?: QuoteVariantCandidate[];
    }) => {
      const sources = resolveMarketDataSourcesForQuoteVariant({
        market_data_sources: input.market_data_sources,
        candidate_brokers: input.candidate_brokers,
        activeResolvedSymbol: input.resolved_symbol,
        quoteCandidates: input.quoteCandidates,
      });
      if (sources.length === 0) return;
      setPendingSymbol(input.symbol.trim());
      setPendingMarketDataSourceChoice({
        symbol: input.symbol.trim(),
        asset_class: input.asset_class,
        instrument_id: input.instrument_id,
        resolved_symbol: input.resolved_symbol?.trim() || undefined,
        sources,
      });
    },
    []
  );

  const openMarketDataSourceChoiceFromRow = useCallback((row: AvailableInstrumentRow) => {
    if (!needsMarketDataSourceChoiceForRow(row)) return;
    openMarketDataSourceChoice({
      symbol: row.symbol,
      asset_class: row.asset_class,
      instrument_id: row.instrument_id,
      market_data_sources: row.market_data_sources,
      candidate_brokers: row.candidate_brokers,
    });
  }, [openMarketDataSourceChoice]);

  const applyResolveResult = useCallback((
    trimmed: string,
    result: ResolveInstrumentResponse,
    options?: ApplyResolveResultOptions
  ) => {
    const tradeSymbol = options?.tradeSymbol?.trim() || trimmed;

    if (result.error === "market_data_connector_required" || result.chart_unavailable_reason === "market_data_connector_required" ||
        result.error === "market_data_preference_unavailable" || result.chart_unavailable_reason === "market_data_preference_unavailable") {
      setPendingSymbol(tradeSymbol);
      setPendingConnectorRequired({
        symbol: result.symbol ?? tradeSymbol,
        asset_class: result.asset_class ?? result.suggested_asset_class,
        message: result.message ?? result.error,
        connect_options: result.connect_options ?? [],
        market_data_sources: result.market_data_sources,
      });
      setState({ kind: "resolved", result });
      return result;
    }

    if (needsAssetClassConfirmation(result)) {
      setPendingSymbol(tradeSymbol);
      setPendingAssetClassPayload({
        error: "market_data_asset_class_required",
        symbol: result.symbol ?? tradeSymbol,
        suggested_asset_class: result.suggested_asset_class ?? result.asset_class,
        valid_asset_classes: result.valid_asset_classes,
        candidate_brokers: result.candidate_brokers,
        market_data_sources: result.market_data_sources,
        requires_market_data_source_choice: result.requires_market_data_source_choice,
        connect_options: result.connect_options,
      });
      setPendingQuoteVariantChoice(null);
      setPendingMarketDataSourceChoice(null);
      setPendingConnectorRequired(null);
      setState({ kind: "resolved", result });
      return result;
    }

    if (needsQuoteVariantConfirmation(result)) {
      setPendingSymbol(tradeSymbol);
      setPendingQuoteVariantChoice({
        tradeSymbol,
        asset_class: result.asset_class,
        instrument_id: result.instrument_id,
        candidates: normalizeQuoteVariantCandidates(result.quote_variant_candidates),
        suggested: result.suggested_quote_variant ?? result.auto_resolved_quote_variant ?? null,
      });
      setPendingMarketDataSourceChoice(null);
      setPendingConnectorRequired(null);
      setState({ kind: "resolved", result });
      return result;
    }

    if (!options?.skipMarketDataSourceChoicePrompt && needsMarketDataSourceChoiceForResolve(result)) {
      if (pendingMarketDataSourceChoiceRef.current?.symbol === tradeSymbol) {
        setState({ kind: "resolved", result });
        return result;
      }
      setPendingSymbol(tradeSymbol);
      setPendingQuoteVariantChoice(null);
      openMarketDataSourceChoice({
        symbol: tradeSymbol,
        asset_class: result.asset_class,
        instrument_id: result.instrument_id,
        resolved_symbol: result.market_data_symbol,
        market_data_sources: result.market_data_sources,
        candidate_brokers: result.candidate_brokers,
        quoteCandidates: normalizeQuoteVariantCandidates(result.quote_variant_candidates),
      });
      setPendingConnectorRequired(null);
      setState({ kind: "resolved", result });
      return result;
    }

    if (resolveRequiresMarketDataConsent(result)) {
      setPendingConsent({
        md_listing_key: result.md_listing_key,
        symbol: tradeSymbol,
        asset_class: result.asset_class,
        market_data_broker: result.market_data_broker,
        attribution: result.market_data_attribution,
        source: result.selected_market_data_source ?? undefined,
      });
      setState({ kind: "resolved", result });
      return result;
    }

    if (
      result.md_supported === false &&
      !hasEffectiveMarketDataSourceSelection(result) &&
      result.resolved !== true
    ) {
      setState({
        kind: "error",
        message: "Market data is not available for this symbol on your broker.",
      });
      return result;
    }

    setPendingSymbol(null);
    clearPendingGates();
    setState({ kind: "resolved", result });
    return result;
  }, [clearPendingGates, openMarketDataSourceChoice]);

  const resolve = useCallback(
    async (symbol: string, assetClass?: string) => {
      const trimmed = symbol.trim();
      if (!trimmed) {
        setState({ kind: "idle" });
        return null;
      }
      if (pendingMarketDataSourceChoiceRef.current?.symbol === trimmed) {
        const current = stateRef.current;
        return current.kind === "resolved" ? current.result : null;
      }
      const dedupeKey = `${brokerAccountCode}|${trimmed}|${assetClass?.trim() ?? ""}`;
      const inflight = resolveInFlightRef.current.get(dedupeKey);
      if (inflight) return inflight;

      const promise = (async () => {
        setState({ kind: "loading" });
        try {
          const result = await resolveInstrument({
            symbol: trimmed,
            asset_class: assetClass,
            broker_account_code: brokerAccountCode || undefined,
          });
          return applyResolveResult(trimmed, result);
        } catch (e) {
          const message = e instanceof Error ? e.message : "Failed to resolve symbol.";
          setState({ kind: "error", message });
          return null;
        }
      })().finally(() => {
        if (resolveInFlightRef.current.get(dedupeKey) === promise) {
          resolveInFlightRef.current.delete(dedupeKey);
        }
      });

      resolveInFlightRef.current.set(dedupeKey, promise);
      return promise;
    },
    [applyResolveResult, brokerAccountCode]
  );

  const saveMarketDataPreference = useCallback(
    async (selection: {
      symbol: string;
      assetClass: string;
      marketDataBroker?: string;
      mdListingKey?: string;
      instrumentId?: string;
      ibkrConid?: number;
      ibkrExchange?: string;
      marketDataSource?: MarketDataSourceOption;
      resolvedSymbol?: string;
    }) => {
      if (selection.marketDataSource) {
        await setMarketDataPreference(
          buildMarketDataPreferenceRequest(
            brokerAccountCode,
            selection.symbol,
            {
              assetClass: selection.assetClass,
              marketDataBroker: selection.marketDataBroker,
              marketDataSource: selection.marketDataSource,
            },
            {
              instrument_id: selection.instrumentId,
              md_listing_key: selection.mdListingKey,
            },
            { resolvedSymbol: selection.resolvedSymbol }
          )
        );
        return;
      }

      await setMarketDataPreference({
        broker_account_code: brokerAccountCode || undefined,
        symbol: selection.symbol,
        consent: true,
        asset_class: selection.assetClass,
        market_data_broker: selection.marketDataBroker,
        md_listing_key: selection.mdListingKey,
        instrument_id: selection.instrumentId,
        ibkr_conid: selection.ibkrConid,
        ibkr_exchange: selection.ibkrExchange,
        resolved_symbol: selection.resolvedSymbol,
      });
    },
    [brokerAccountCode]
  );

  const resolveAfterPreferenceSaved = useCallback(
    async (
      tradeSymbol: string,
      assetClass?: string,
      options?: {
        resolvedSymbol?: string;
        quoteCandidates?: QuoteVariantCandidate[];
      }
    ) => {
      setPendingMarketDataSourceChoice(null);
      setPendingQuoteVariantChoice(null);
      setState({ kind: "loading" });
      try {
        const result = await resolveInstrumentForOhlcMenu(
          buildInstrumentResolveRequest({
            tradeSymbol,
            assetClass,
            brokerAccountCode,
            resolvedSymbol: options?.resolvedSymbol,
            quoteCandidates: options?.quoteCandidates,
          })
        );
        return applyResolveResult(tradeSymbol, result, {
          skipMarketDataSourceChoicePrompt: true,
          tradeSymbol,
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to resolve symbol.";
        setState({ kind: "error", message });
        return null;
      }
    },
    [applyResolveResult, brokerAccountCode]
  );

  const confirmQuoteVariantSelection = useCallback(
    async (qualifiedSymbol: string) => {
      const pending = pendingQuoteVariantChoice;
      if (!pending) return null;
      const normalized = normalizeQuoteVariantCandidates(pending.candidates);
      const resolvedSymbol = resolvedSymbolForInstrumentResolve(qualifiedSymbol, normalized);
      if (!resolvedSymbol) return null;

      setSavingQuoteVariant(true);
      try {
        const assetClass = pending.asset_class ?? "";
        const preview = await resolveInstrumentForOhlcMenu(
          buildInstrumentResolveRequest({
            tradeSymbol: pending.tradeSymbol,
            assetClass: assetClass || undefined,
            brokerAccountCode,
            resolvedSymbol: qualifiedSymbol,
            quoteCandidates: normalized,
          })
        );

        const sources = resolveMarketDataSourcesForQuoteVariant({
          market_data_sources: preview.market_data_sources,
          candidate_brokers: preview.candidate_brokers,
          activeResolvedSymbol: resolvedSymbol,
          quoteCandidates: normalized,
        });

        if (sources.length > 1) {
          setPendingQuoteVariantChoice(null);
          openMarketDataSourceChoice({
            symbol: pending.tradeSymbol,
            asset_class: assetClass || preview.asset_class,
            instrument_id: preview.instrument_id ?? pending.instrument_id,
            resolved_symbol: resolvedSymbol,
            market_data_sources: sources,
            candidate_brokers: preview.candidate_brokers,
            quoteCandidates: normalized,
          });
          return applyResolveResult(pending.tradeSymbol, preview, {
            tradeSymbol: pending.tradeSymbol,
            skipMarketDataSourceChoicePrompt: true,
          });
        }

        if (sources.length === 1) {
          const source = sources[0];
          const marketDataBroker =
            source?.market_data_broker ??
            source?.broker ??
            preview.market_data_broker;

          await saveMarketDataPreference({
            symbol: pending.tradeSymbol,
            assetClass: assetClass || preview.asset_class || "",
            marketDataSource: source,
            marketDataBroker: marketDataBroker?.trim() || undefined,
            instrumentId: preview.instrument_id ?? pending.instrument_id,
            resolvedSymbol,
          });

          return resolveAfterPreferenceSaved(
            pending.tradeSymbol,
            assetClass || preview.asset_class || undefined,
            { resolvedSymbol, quoteCandidates: normalized }
          );
        }

        return applyResolveResult(pending.tradeSymbol, preview, {
          tradeSymbol: pending.tradeSymbol,
        });
      } finally {
        setSavingQuoteVariant(false);
      }
    },
    [
      applyResolveResult,
      brokerAccountCode,
      openMarketDataSourceChoice,
      pendingQuoteVariantChoice,
      resolveAfterPreferenceSaved,
      saveMarketDataPreference,
    ]
  );

  const confirmMarketDataSourceSelection = useCallback(
    async (source: MarketDataSourceOption) => {
      const pending = pendingMarketDataSourceChoice;
      const symbol = pending?.symbol ?? pendingSymbol;
      if (!symbol) return null;

      const marketDataBroker = (source.market_data_broker ?? source.broker)?.trim();
      const assetClass =
        pending?.asset_class ??
        (state.kind === "resolved" ? state.result.asset_class : undefined) ??
        "";

      setSavingMarketDataSource(true);
      try {
        const resolvedSymbol =
          pending?.resolved_symbol?.trim() ||
          (state.kind === "resolved" ? state.result.market_data_symbol?.trim() : undefined);

        await setMarketDataPreference(
          buildMarketDataPreferenceRequest(
            brokerAccountCode,
            symbol,
            { assetClass, marketDataSource: source, marketDataBroker: marketDataBroker || undefined },
            { instrument_id: pending?.instrument_id },
            { resolvedSymbol }
          )
        );
        return resolveAfterPreferenceSaved(symbol, assetClass || undefined);
      } finally {
        setSavingMarketDataSource(false);
      }
    },
    [
      brokerAccountCode,
      pendingMarketDataSourceChoice,
      pendingSymbol,
      resolveAfterPreferenceSaved,
      state,
    ]
  );

  const confirmAssetClassSelection = useCallback(
    async (selection: {
      assetClass: string;
      marketDataBroker?: string;
      marketDataSource?: MarketDataSourceOption;
    }) => {
      const symbol = pendingSymbol ?? pendingAssetClassPayload?.symbol;
      if (!symbol) return null;

      const resolved = state.kind === "resolved" ? state.result : null;
      const source = selection.marketDataSource;
      const marketDataBroker =
        source?.market_data_broker ??
        source?.broker ??
        selection.marketDataBroker;

      if (source) {
        await setMarketDataPreference(
          buildMarketDataPreferenceRequest(
            brokerAccountCode,
            symbol,
            {
              assetClass: selection.assetClass,
              marketDataSource: source,
              marketDataBroker: marketDataBroker || undefined,
            },
            {
              instrument_id: resolved?.instrument_id,
              md_listing_key: resolved?.md_listing_key,
            }
          )
        );
      } else {
        await saveMarketDataPreference({
          symbol,
          assetClass: selection.assetClass,
          marketDataBroker: marketDataBroker || undefined,
          mdListingKey: resolved?.md_listing_key,
          instrumentId: resolved?.instrument_id,
        });
      }

      setPendingAssetClassPayload(null);
      return resolveAfterPreferenceSaved(symbol, selection.assetClass);
    },
    [
      brokerAccountCode,
      pendingAssetClassPayload?.symbol,
      pendingSymbol,
      resolveAfterPreferenceSaved,
      saveMarketDataPreference,
      state,
    ]
  );

  const confirmAssetClass = useCallback(
    async (assetClass: string) => {
      return confirmAssetClassSelection({ assetClass });
    },
    [confirmAssetClassSelection]
  );

  const grantConsent = useCallback(async () => {
    if (!pendingConsent?.md_listing_key) return;
    const symbol = pendingConsent.symbol;
    const assetClass = pendingConsent.asset_class;
    await saveMarketDataPreference({
      symbol: pendingConsent.symbol,
      assetClass: pendingConsent.asset_class ?? "",
      marketDataBroker: pendingConsent.market_data_broker,
      mdListingKey: pendingConsent.md_listing_key,
      marketDataSource: pendingConsent.source,
    });
    setPendingConsent(null);
    return resolveAfterPreferenceSaved(symbol, assetClass || undefined);
  }, [pendingConsent, resolveAfterPreferenceSaved, saveMarketDataPreference]);

  const dismissConnectorRequired = useCallback(() => {
    setPendingConnectorRequired(null);
  }, []);

  const openCentralSourceFromConnectorGate = useCallback(() => {
    const pending = pendingConnectorRequired;
    if (!pending?.market_data_sources?.length) return;
    openMarketDataSourceChoice({
      symbol: pending.symbol,
      asset_class: pending.asset_class,
      market_data_sources: pending.market_data_sources,
    });
    setPendingConnectorRequired(null);
  }, [openMarketDataSourceChoice, pendingConnectorRequired]);

  const seedFromAvailableInstrumentRow = useCallback((row: AvailableInstrumentRow) => {
    if (!isInstrumentMarketDataReadyForCharts(row)) return;
    const result = availableInstrumentRowToResolveResult(row);
    setPendingSymbol(row.symbol.trim());
    clearPendingGates();
    setState({ kind: "resolved", result });
  }, [clearPendingGates]);

  const reset = useCallback(() => {
    setState({ kind: "idle" });
    setPendingSymbol(null);
    clearPendingGates();
  }, [clearPendingGates]);

  const resolvedResult = state.kind === "resolved" ? state.result : null;
  const mdBlocked = resolvedResult?.md_supported === false || state.kind === "error";

  const marketDataSourceOptionsForModal =
    pendingMarketDataSourceChoice?.sources ??
    (pendingAssetClassPayload
      ? resolveMarketDataSourceOptions({
          market_data_sources: pendingAssetClassPayload.market_data_sources,
          candidate_brokers: pendingAssetClassPayload.candidate_brokers,
        })
      : []);

  return {
    state,
    resolve,
    seedFromAvailableInstrumentRow,
    confirmAssetClass,
    confirmAssetClassSelection,
    confirmMarketDataSourceSelection,
    confirmQuoteVariantSelection,
    saveMarketDataPreference,
    grantConsent,
    reset,
    dismissConnectorRequired,
    openCentralSourceFromConnectorGate,
    openMarketDataSourceChoice,
    openMarketDataSourceChoiceFromRow,
    pendingAssetClassPayload,
    pendingQuoteVariantChoice,
    pendingMarketDataSourceChoice,
    pendingConsent,
    pendingConnectorRequired,
    resolvedResult,
    mdBlocked,
    savingMarketDataSource,
    savingQuoteVariant,
    validCandleTimeframes: resolvedResult?.valid_candle_timeframes,
    validAssetClasses:
      pendingAssetClassPayload?.valid_asset_classes ?? resolvedResult?.valid_asset_classes,
    suggestedAssetClass:
      pendingAssetClassPayload?.suggested_asset_class ?? resolvedResult?.suggested_asset_class,
    candidateBrokers:
      (pendingAssetClassPayload?.candidate_brokers ??
        resolvedResult?.candidate_brokers) as MarketDataCandidateBroker[] | undefined,
    marketDataSourceOptions:
      marketDataSourceOptionsForModal.length > 0
        ? marketDataSourceOptionsForModal
        : resolveMarketDataSourceOptions({
            market_data_sources: resolvedResult?.market_data_sources,
            candidate_brokers: resolvedResult?.candidate_brokers,
          }),
    defaultMarketDataSourceOption: defaultMarketDataSourceOption(
      pendingMarketDataSourceChoice?.sources ??
        resolveMarketDataSourceOptions({
          market_data_sources: pendingAssetClassPayload?.market_data_sources,
          candidate_brokers: pendingAssetClassPayload?.candidate_brokers,
        })
    ),
    resolvedAssetClass: resolvedResult?.asset_class,
    isIbkrMarketDataBroker,
  };
};
